import { LitElement } from '@lit'
import { viewportObserver } from '@nova/utils'

class NovaProductGallery extends LitElement {
  static properties = {
    viewport: { type: String, state: true },
    galleryCarousel: { type: Object, state: true },
    thumbnailCarousel: { type: Object, state: true },
    thumbnailPrevious: { type: Object, state: true },
    thumbnailNext: { type: Object, state: true },
    zoomInOverlay: { type: Object, state: true },
  }

  constructor() {
    super()
    this.viewport = viewportObserver.currentViewport
    viewportObserver.addListener(this._handleViewportChange)
    this.galleryCarousel = null
    this.thumbnailCarousel = null
    this.thumbnailPrevious = null
    this.thumbnailNext = null
    this.zoomInOverlay = null
    this.observer = null
  }

  connectedCallback() {
    super.connectedCallback()

    document.addEventListener('nova:product:render-gallery', (event) => {
      this.updateGalleryContent(event.detail.html)
    })
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    viewportObserver.removeListener(this._handleViewportChange)
    document.removeEventListener('nova:product:render-gallery', (event) => {
      this.updateGalleryContent(event.detail.html)
    })

    if (this.observer) {
      this.observer.disconnect()
    }
  }

  firstUpdated() {
    this.initialize()
  }

  initialize() {
    this.thumbnailCarousel = this.querySelector('.js-thumbnails-carousel')
    this.galleryCarousel = this.querySelector('.js-gallery-carousel')
    this.thumbnailPrevious = this.thumbnailCarousel.shadowRoot.querySelector(
      '.carousel__navigation-button--previous',
    )
    this.thumbnailNext = this.thumbnailCarousel.shadowRoot.querySelector(
      '.carousel__navigation-button--next',
    )
    this.hideDisabledButton(this.thumbnailPrevious)
    this.hideDisabledButton(this.thumbnailNext)
    this.handleThumbnailCarouselAttribute()
    this.handleThumbnailCarouselClick()
    this.handleGalleryCarouselMove()
    this.handleZoomOnImage(3)
    this.initMutationObserver()

    // HACK: Delay execution to allow variant and DOM to update media gallery
    requestAnimationFrame(() => {
      this.resetCarousel()
      this.removeCarouselItemAttributes()
    })
  }

  initMutationObserver() {
    if (this.observer) {
      this.observer.disconnect()
    }
    const handleClassChange = (mutationsList) => {
      for (const mutation of mutationsList) {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'class'
        ) {
          this.hideDisabledButton(this.thumbnailPrevious)
          this.hideDisabledButton(this.thumbnailNext)
        }
      }
    }
    this.observer = new MutationObserver(handleClassChange)
    if (this.thumbnailPrevious) {
      this.observer.observe(this.thumbnailPrevious, { attributes: true })
    }
    if (this.thumbnailNext) {
      this.observer.observe(this.thumbnailNext, { attributes: true })
    }
  }

  hideDisabledButton(button) {
    if (button) {
      if (button.classList.contains('carousel__navigation-button--disabled')) {
        button.style.opacity = 0
      } else {
        button.style.opacity = 1
      }
    }
  }

  resetCarousel() {
    const carousels = this.querySelectorAll('sl-carousel')
    for (const carousel of carousels) {
      carousel.goToSlide(0)
    }
  }

  updateGalleryContent(content) {
    if (!content) return
    // Clear existing children
    while (this.firstChild) {
      this.removeChild(this.firstChild)
    }
    // Append new children
    while (content.firstChild) {
      this.appendChild(content.firstChild)
    }
    this.requestUpdate()
    this.initialize()
    // Remove attributes after content update
    requestAnimationFrame(() => {
      this.removeCarouselItemAttributes()
    })
  }

  _handleViewportChange = (newViewport) => {
    this.viewport = newViewport
    this.handleThumbnailCarouselAttribute()
  }

  handleThumbnailCarouselAttribute() {
    if (!this.thumbnailCarousel) return

    const slidesPerPage = 5
    this.thumbnailCarousel.setAttribute('slides-per-page', slidesPerPage)
  }

  handleThumbnailCarouselClick() {
    if (!this.thumbnailCarousel) return

    const handleEvent = (event) => {
      if (event.type === 'click' || event.key === 'Enter') {
        const card = event.target.closest('.thumbnails-item')
        if (card) {
          this.handleGalleryCarouselDisplay(card)
        }
      }
    }

    this.thumbnailCarousel.addEventListener('click', handleEvent)
    this.thumbnailCarousel.addEventListener('keydown', handleEvent)
  }

  handleGalleryCarouselDisplay(card) {
    if (!this.galleryCarousel || card.classList.contains('active')) return

    const activeCard = this.thumbnailCarousel.querySelector('.active')
    if (activeCard) activeCard.classList.remove('active')

    card.classList.add('active')
    const cardPosition = card.dataset.position
    if (cardPosition) {
      const displayCardIndex = Number(cardPosition) - 1
      this.galleryCarousel.goToSlide(displayCardIndex)
    }
  }

  handleGalleryCarouselMove() {
    if (!this.galleryCarousel || !this.thumbnailCarousel) return

    this.galleryCarousel.addEventListener('sl-slide-change', (event) => {
      const activeGalleryIndex = event.detail.index
      this.handleVideoAutoPlay(event.detail.slide)
      this.thumbnailCarousel.goToSlide(Number(activeGalleryIndex))
      const oldActiveThumbnail = this.thumbnailCarousel.querySelector('.active')
      if (oldActiveThumbnail) oldActiveThumbnail.classList.remove('active')
      const newActiveThumbnail = this.thumbnailCarousel.querySelector(
        `[data-position="${activeGalleryIndex + 1}"]`,
      )
      if (newActiveThumbnail) newActiveThumbnail.classList.add('active')
    })
  }

  handleVideoAutoPlay(slide) {
    const media = slide.querySelector('nova-adaptive-video')
    if (!media) return
    media.play()
  }

  zoomMoveWithHover(image, event, zoomRatio) {
    const ratio = image.height / image.width
    const container = event.target.getBoundingClientRect()
    const xPosition = event.clientX - container.left
    const yPosition = event.clientY - container.top
    const xPercent = `${xPosition / (image.clientWidth / 100)}%`
    const yPercent = `${yPosition / ((image.clientWidth * ratio) / 100)}%`

    this.zoomInOverlay.style.backgroundPosition = `${xPercent} ${yPercent}`
    this.zoomInOverlay.style.backgroundSize = `${image.width * zoomRatio}px`
  }

  handleZoomEvents(image, zoomRatio) {
    if (this.viewport === 'mobile' || this.viewport === 'tablet') {
      this.zoomInOverlay = image
        .closest('.img')
        .querySelector('.zoom-mobile-overlay')
      if (!this.zoomInOverlay) return
      this.zoomInOverlay.classList.remove('hidden')

      this.zoomInOverlay.addEventListener(
        'click',
        this.handleMobileOverlayRemove.bind(this),
      )
    } else {
      this.zoomInOverlay = image.closest('.img').querySelector('.zoom-overlay')
      if (!this.zoomInOverlay) return
      this.zoomInOverlay.classList.remove('hidden')

      const carouselItem = image.closest('.gallery-item')
      if (carouselItem && !carouselItem.classList.contains('zoomed'))
        carouselItem.classList.add('zoomed')

      // Toggle aria-hidden for zoom buttons
      this.toggleZoomButtonsAriaHidden(carouselItem, true)

      this.zoomInOverlay.addEventListener(
        'click',
        this.handleOverlayRemove.bind(this, carouselItem),
      )
      this.zoomInOverlay.addEventListener('mousemove', (event) =>
        this.zoomMoveWithHover(image, event, zoomRatio),
      )
      this.zoomInOverlay.addEventListener(
        'mouseleave',
        this.handleOverlayRemove.bind(this, carouselItem),
      )
    }
  }

  handleMobileOverlayRemove() {
    if (
      this.zoomInOverlay &&
      !this.zoomInOverlay.classList.contains('hidden')
    ) {
      this.zoomInOverlay.classList.add('hidden')

      this.zoomInOverlay.removeEventListener(
        'click',
        this.handleMobileOverlayRemove.bind(this),
      )

      this.zoomInOverlay = null
    }
  }

  handleOverlayRemove(carouselItem) {
    if (
      this.zoomInOverlay &&
      !this.zoomInOverlay.classList.contains('hidden')
    ) {
      this.zoomInOverlay.classList.add('hidden')

      this.zoomInOverlay.removeEventListener(
        'click',
        this.handleOverlayRemove.bind(this, carouselItem),
      )
      this.zoomInOverlay.removeEventListener(
        'mousemove',
        this.zoomMoveWithHover,
      )
      this.zoomInOverlay.removeEventListener(
        'mouseleave',
        this.handleOverlayRemove.bind(this, carouselItem),
      )

      this.zoomInOverlay = null
    }
    if (carouselItem) {
      carouselItem.classList.remove('zoomed')
      // Reset aria-hidden for zoom buttons
      this.toggleZoomButtonsAriaHidden(carouselItem, false)
    }
  }

  toggleZoomButtonsAriaHidden(carouselItem, isZoomed) {
    const zoomInButton = carouselItem.querySelector('.product__zoom-in-button')
    const zoomOutButton = carouselItem.querySelector('.product__zoom-out-button')

    if (zoomInButton) {
      zoomInButton.setAttribute('aria-hidden', isZoomed.toString())
    }
    if (zoomOutButton) {
      zoomOutButton.setAttribute('aria-hidden', (!isZoomed).toString())
    }
  }

  handleZoomOnImage(zoomRatio) {
    const zoomInButtons = this.querySelectorAll('.product__zoom-in-button')
    for (const zoomInButton of zoomInButtons) {
      zoomInButton.addEventListener('click', () => {
        const image = zoomInButton.parentElement.querySelector('.zoom-image')
        this.handleZoomEvents(image, zoomRatio)
      })
    }

    const zoomOutButtons = this.querySelectorAll('.product__zoom-out-button')
    for (const zoomOutButton of zoomOutButtons) {
      zoomOutButton.addEventListener('click', () => {
        const carouselItem = zoomOutButton.closest('.gallery-item')
        if (carouselItem) {
          this.handleOverlayRemove(carouselItem)
        }
      })
    }
  }

  removeCarouselItemAttributes() {
    const carouselItems = this.querySelectorAll('sl-carousel-item')
    for (const item of carouselItems) {
      item.removeAttribute('role')
      item.removeAttribute('aria-label')
    }
  }

  createRenderRoot() {
    return this
  }
}

customElements.define('nova-product-gallery', NovaProductGallery)
